import { updateSubjectApi } from "@/api/services/admin/learning-content";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { STATUS_LABEL } from "@/constants/enum";
import { BasicInfoForm } from "@/features/learning-content/components/SubjectForm/BasicInfoForm";

import { TextExplanationForm } from "@/features/learning-content/components/SubjectForm/TextExplanationForm";
import { WordProblemDataForm } from "@/features/learning-content/components/SubjectForm/WordProblemDataForm";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useSubjectDetail } from "@/features/learning-content/hooks/useSubjectDetail";
import { subjectSchema } from "@/features/learning-content/schema/SubjectSchema";
import { TSubjectCreateForm } from "@/features/learning-content/types/learningContent";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { dayjs } from "@/utils/dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, CircularProgress, Stack, Typography, styled } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TSubjectEditPageProps = {};

// Subject edit page - edits an existing subject under an industry/category
export const SubjectEditPage: FC<TSubjectEditPageProps> = () => {
  const { id, subjectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const { data: industryData } = useIndustryDetail(id);
  const { data: subjectData, isLoading: isLoadingSubject } = useSubjectDetail(subjectId);

  const categoryName = industryData?.name ?? "";

  // Map API response to form values
  const initialValues = useMemo<TSubjectCreateForm>(() => {
    if (!subjectData) {
      return {
        subjectName: "",
        internalNote: null,
        status: STATUS_LABEL.DISABLED,
        activationDate: "--/--/--",
        vocabularySpreadsheetUrl: "",
        problemSpreadsheetUrl: "",
        problemScoreAllocation: "",
        sectionDivision: "",
        mockExamQuestionCount: "",
        fullScore: "",
        passingLineQuestionCount: "",
        passingScore: "",
        textExplanationFile: null,
        textExplanationFileUrl: null,
        textExplanationPageCount: 0,
        textExplanationInternalNote: null,
      };
    }

    const questionScore = subjectData.questionScore ?? 0;
    const mockExamCount = subjectData.mockExamQuestionCount ?? 0;
    const calculatedFullScore = questionScore > 0 && mockExamCount > 0 ? questionScore * mockExamCount : 0;
    const passingLineCount = subjectData.passingLineQuestionCount ?? 0;
    const calculatedPassingScore = questionScore > 0 && passingLineCount > 0 ? questionScore * passingLineCount : 0;

    return {
      subjectName: subjectData.name,
      internalNote: subjectData.internalMemo ?? null,
      status: subjectData.isActive ? STATUS_LABEL.ENABLED : STATUS_LABEL.DISABLED,
      activationDate: subjectData.activationDate ? dayjs(subjectData.activationDate).format("YYYY/MM/DD") : "--/--/--",
      vocabularySpreadsheetUrl: subjectData.vocabularySpreadsheetUrl ?? "",
      problemSpreadsheetUrl: subjectData.questionSpreadsheetUrl ?? "",
      problemScoreAllocation: subjectData.questionScore?.toString() ?? "",
      sectionDivision: subjectData.partType ?? "",
      mockExamQuestionCount: subjectData.mockExamQuestionCount?.toString() ?? "",
      fullScore: calculatedFullScore > 0 ? calculatedFullScore.toString() : (subjectData.totalScore?.toString() ?? ""),
      passingLineQuestionCount: subjectData.passingLineQuestionCount?.toString() ?? "",
      passingScore: calculatedPassingScore > 0 ? calculatedPassingScore.toString() : (subjectData.passingScore?.toString() ?? ""),
      textExplanationFile: null,
      textExplanationFileUrl: subjectData.textbookFileUrl ?? null,
      textExplanationPageCount: subjectData.textbookPageCount ?? 0,
      textExplanationInternalNote: subjectData.textbookMemo ?? null,
    };
  }, [subjectData]);

  const methods = useForm<TSubjectCreateForm, object, TSubjectCreateForm>({
    defaultValues: initialValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<TSubjectCreateForm>(subjectSchema as any),
    mode: "all",
    reValidateMode: "onBlur",
  });

  // Reset form when API data loads
  useEffect(() => {
    if (subjectData) {
      methods.reset(initialValues);
    }
  }, [subjectData, initialValues, methods]);

  const onSubmit = async (data: TSubjectCreateForm) => {
    if (!subjectId) return;

    setLoading(true);
    try {
      await updateSubjectApi(subjectId, {
        name: data.subjectName,
        internalMemo: data.internalNote || null,
        vocabularySpreadsheetUrl: data.vocabularySpreadsheetUrl || null,
        questionSpreadsheetUrl: data.problemSpreadsheetUrl || null,
        questionScore: data.problemScoreAllocation ? Number(data.problemScoreAllocation) : null,
        partType: data.sectionDivision || null,
        mockExamQuestionCount: data.mockExamQuestionCount ? Number(data.mockExamQuestionCount) : null,
        passingLineQuestionCount: data.passingLineQuestionCount ? Number(data.passingLineQuestionCount) : null,
        totalScore: data.fullScore ? Number(data.fullScore) : null,
        passingScore: data.passingScore ? Number(data.passingScore) : null,
        textbookFileUrl: data.textExplanationFile?.filePath || data.textExplanationFileUrl || null,
        textbookPageCount: data.textExplanationPageCount || null,
        textbookMemo: data.textExplanationInternalNote || null,
      });

      toast.success(API_SUCCESS_MESSAGE);

      if (id && subjectId) {
        navigate(getDynamicRoute(AppRoutes.learningContentSubjectDetail, { id, subjectId }));
      }
    } catch (error) {
      showError(error);
    }
    setLoading(false);
  };

  const handleBack = () => {
    if (id && subjectId) {
      navigate(getDynamicRoute(AppRoutes.learningContentSubjectDetail, { id, subjectId }));
    } else if (id) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  // Show loading state while fetching subject detail
  if (isLoadingSubject) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EditWrapper gap={4}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            {categoryName}-WEEK情報の編集
          </Typography>
        }
        description={
          <Typography variant="body14">
            分野情報や同期先スプレッドシートを変更できます。コンテンツや試験情報の変更後は、保存後に同期して問題ないことを確認してください。
          </Typography>
        }
        enablePageBack={false}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={subjectSchema} initialValues={initialValues}>
        <MenuNavigate
          menuPrefix={
            <Box sx={{ p: "16px 16px 12px 16px" }}>
              <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
                {subjectData?.name ?? ""}
              </Typography>
            </Box>
          }
          contents={[
            {
              title: "基本情報",
              content: <BasicInfoForm />,
            },
            {
              title: "単語・問題データ",
              content: <WordProblemDataForm />,
            },
            {
              title: "テキスト解説",
              content: <TextExplanationForm />,
            },
          ]}
          contentWidth="900px"
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Button size="sm-round" variant="secondary" onClick={handleBack}>
                  戻る
                </Button>
                <Button disabled={loading} size="sm-round" type="submit">
                  保存
                </Button>
              </Stack>
            </FooterContainer>
          }
        />
      </Form>
    </EditWrapper>
  );
};

const EditWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
